class QuickNavBar extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          pointer-events: none;
        }
        .placeholder {
          pointer-events: none;
        }
      </style>
      <div class="placeholder"></div>
    `;
    
    this.navPoints = [];
    this.productPage = null;
    this.stickyHeader = null;
    this.quickNavElement = null;
    this.quickNavPointsContainer = null;
    this.isVisible = false;
    this.currentScrollTop = 0;
    this.scrollThreshold = 50;
    this.headerHeight = 0;
    this.isHeaderVisible = false;
    this.anchorOffset = 80;
    this.shopifyChatElement = null;
    this.shopifyChatObserver = null;
    this.shopifyChatOriginalBottom = null;
  }
  
  connectedCallback() {
    this.setupEventListeners();
    
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.initScrollHandling();
      });
    } else {
      this.initScrollHandling();
    }
    
    this.initShopifyChatMonitoring();
  }
  
  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScrollHandler);
    window.removeEventListener('resize', this.onResizeHandler);
    
    if (this.headerObserver) {
      this.headerObserver.disconnect();
    }
    
    if (this.shopifyChatObserver) {
      this.shopifyChatObserver.disconnect();
    }
  }
  
  setupEventListeners() {
    this.quickNavElement = document.querySelector('.quick-nav-bar');
    this.quickNavPointsContainer = document.querySelector('.quick-nav-points');
    
    this.onScrollHandler = this.onScroll.bind(this);
    this.onResizeHandler = this.onResize.bind(this);
    
    window.addEventListener('scroll', this.onScrollHandler, false);
    window.addEventListener('resize', this.onResizeHandler, false);
    
    this.onResize();
  }
  
  initScrollHandling() {
    this.productPage = document.querySelector('.product-page');
    this.stickyHeader = document.querySelector('sticky-header');
    
    if (this.productPage) {
      this.productPageBounds = this.productPage.getBoundingClientRect();
    }
    
    this.observeHeaderChanges();
    
    this.scanAnchors();
    
    requestAnimationFrame(() => {
      this.updateHeaderHeight();
      this.onScroll();
    });
    
    setTimeout(() => {
      this.updateHeaderHeight();
      this.updateNavPosition();
    }, 100);
  }
  
  observeHeaderChanges() {
    const headerElement = document.querySelector('.section-header');
    if (!headerElement) return;
    
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          this.updateHeaderHeight();
          this.updateNavPosition();
        }
      });
    });
    
    observer.observe(headerElement, {
      attributes: true,
      attributeFilter: ['class']
    });
    
    this.headerObserver = observer;
  }
  
  scanAnchors() {
    const anchors = document.querySelectorAll('.quick-nav-anchor');
    this.navPoints = [];
    
    if (!this.quickNavPointsContainer) {
      return;
    }
    
    this.quickNavPointsContainer.innerHTML = '';
    
    anchors.forEach((anchor, index) => {
      const anchorId = anchor.id;
      const label = anchor.getAttribute('data-label');
      
      if (anchorId && label) {
        this.navPoints.push({
          label: label,
          target: anchorId
        });
        
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'quick-nav-point';
        button.setAttribute('data-target', anchorId);
        button.setAttribute('aria-label', label);
        button.textContent = label;
        
        const clickHandler = (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.scrollToAnchor(anchorId);
        };
        
        button.addEventListener('click', clickHandler);
        
        this.quickNavPointsContainer.appendChild(button);
      }
    });
  }
  
  updateHeaderHeight() {
    const headerElement = document.querySelector('.section-header');
    const isHeaderHidden = headerElement && headerElement.classList.contains('shopify-section-header-hidden');
    
    if (isHeaderHidden) {
      this.headerHeight = 0;
      this.isHeaderVisible = false;
    } else {
      this.headerHeight = headerElement ? headerElement.offsetHeight : 0;
      this.isHeaderVisible = true;
    }
    
    this.updateNavPosition();
  }
  
  onResize() {
    this.updateHeaderHeight();
    if (this.productPage) {
      this.productPageBounds = this.productPage.getBoundingClientRect();
    }
  }
  
  onScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    this.currentScrollTop = scrollTop;
    
    this.updateHeaderHeight();
    
    if (this.productPage) {
      const productPageTop = this.productPage.getBoundingClientRect().top + scrollTop;
      const scrollPastThreshold = scrollTop > (productPageTop + this.scrollThreshold);
      
      if (scrollPastThreshold && !this.isVisible) {
        this.show();
      } else if (!scrollPastThreshold && this.isVisible) {
        this.hide();
      }
    }
    
    this.updateActiveNavPoint();
  }
  
  updateNavPosition() {
    if (!this.quickNavElement) {
      this.quickNavElement = document.querySelector('.quick-nav-bar');
    }
    
    if (this.quickNavElement) {
      const isMobile = window.innerWidth <= 768;
      
      if (isMobile) {
        if (this.isVisible) {
          this.quickNavElement.style.bottom = '0px';
          this.quickNavElement.style.top = 'auto';
        } else {
          this.quickNavElement.style.bottom = '-100px';
          this.quickNavElement.style.top = 'auto';
        }
      } else {
        let targetTop = '0px';
        
        if (this.isHeaderVisible && this.headerHeight > 0) {
          targetTop = `${this.headerHeight}px`;
        }
        
        if (this.isVisible) {
          this.quickNavElement.style.top = targetTop;
          this.quickNavElement.style.bottom = 'auto';
        } else {
          this.quickNavElement.style.top = '-100px';
          this.quickNavElement.style.bottom = 'auto';
        }
      }
    }
  }
  
  show() {
    if (!this.quickNavElement) {
      this.quickNavElement = document.querySelector('.quick-nav-bar');
    }
    
    if (this.quickNavElement) {
      this.quickNavElement.classList.add('quick-nav-bar--visible');
      this.isVisible = true;
      this.updateNavPosition();
      
      if (window.innerWidth <= 768) {
        document.body.classList.add('quick-nav-visible');
        this.adjustShopifyChatPosition();
      }
    }
  }
  
  hide() {
    if (!this.quickNavElement) {
      this.quickNavElement = document.querySelector('.quick-nav-bar');
    }
    
    if (this.quickNavElement) {
      this.quickNavElement.classList.remove('quick-nav-bar--visible');
      this.isVisible = false;
      this.updateNavPosition();
      document.body.classList.remove('quick-nav-visible');
      this.adjustShopifyChatPosition();
    }
  }
  
  scrollToAnchor(anchorId) {
    if (!anchorId) {
      return;
    }
    
    const anchorElement = document.getElementById(anchorId);
    if (!anchorElement) {
      return;
    }
    
    const anchorRect = anchorElement.getBoundingClientRect();
    const anchorTop = anchorRect.top + window.pageYOffset;
    const offset = this.headerHeight + 20;
    const targetPosition = anchorTop - offset;
    
    window.scrollTo({
      top: targetPosition,
      behavior: 'smooth'
    });
  }
  
  updateActiveNavPoint() {
    const navButtons = document.querySelectorAll('.quick-nav-point');
    if (navButtons.length === 0) return;
    
    let activeTargetId = null;
    const scrollPosition = window.pageYOffset + this.headerHeight + 100;
    
    for (const navPoint of this.navPoints) {
      const anchorElement = document.getElementById(navPoint.target);
      if (!anchorElement) continue;
      
      const anchorTop = anchorElement.getBoundingClientRect().top + window.pageYOffset;
      
      if (scrollPosition >= anchorTop) {
        activeTargetId = navPoint.target;
      }
    }
    
    navButtons.forEach(button => {
      const buttonTargetId = button.getAttribute('data-target');
      if (buttonTargetId === activeTargetId) {
        button.classList.add('quick-nav-point--active');
      } else {
        button.classList.remove('quick-nav-point--active');
      }
    });
  }
  
  initShopifyChatMonitoring() {
    const maxMonitoringTime = 10000;
    let startTime = Date.now();
    
    const checkShopifyChat = () => {
      const shopifyChat = document.querySelector('#ShopifyChat');
      if (shopifyChat) {
        this.shopifyChatElement = shopifyChat;
        this.setupShopifyChatObserver();
        this.adjustShopifyChatPosition();
        return true;
      }
      return false;
    };
    
    const monitorShopifyChat = () => {
      const elapsedTime = Date.now() - startTime;
      
      if (elapsedTime >= maxMonitoringTime) {
        return;
      }
      
      if (!checkShopifyChat()) {
        setTimeout(monitorShopifyChat, 200);
      }
    };
    
    if (!checkShopifyChat()) {
      monitorShopifyChat();
    }
  }
  
  setupShopifyChatObserver() {
    if (!this.shopifyChatElement) return;
    
    this.shopifyChatObserver = new MutationObserver((mutations) => {
      let shouldAdjust = false;
      
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          shouldAdjust = true;
        }
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          shouldAdjust = true;
        }
      });
      
      if (shouldAdjust && window.innerWidth <= 768) {
        requestAnimationFrame(() => {
          this.adjustShopifyChatPosition();
        });
      }
    });
    
    this.shopifyChatObserver.observe(this.shopifyChatElement, {
      attributes: true,
      attributeFilter: ['style'],
      childList: true,
      subtree: true
    });
  }
  
  adjustShopifyChatPosition() {
    if (!this.shopifyChatElement || window.innerWidth > 768) return;
    
    const computedStyle = window.getComputedStyle(this.shopifyChatElement);
    const currentBottom = computedStyle.bottom;
    
    if (this.shopifyChatOriginalBottom === null) {
      this.shopifyChatOriginalBottom = currentBottom;
    }
    
    const quickNavHeight = 100;
    
    if (this.isVisible) {
      const originalValue = parseFloat(this.shopifyChatOriginalBottom) || 16;
      const newBottom = originalValue + quickNavHeight;
      this.shopifyChatElement.style.setProperty('bottom', `${newBottom}px`, 'important');
    } else {
      this.shopifyChatElement.style.setProperty('bottom', this.shopifyChatOriginalBottom, 'important');
    }
  }
}

customElements.define('quick-nav-bar', QuickNavBar);